import { ThemeProvider } from "next-themes";
import { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import AuthCallback from "./pages/AuthCallback";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import { ADMIN_ROLES, AuthProvider, Role, useAuth, WRITE_ROLES } from "./lib/auth";
import Admin from "./pages/Admin";
import Approvals from "./pages/Approvals";
import Cash from "./pages/Cash";
import CashMonthly from "./pages/CashMonthly";
import Expenses from "./pages/Expenses";
import CashFlow from "./pages/CashFlow";
import Dashboard from "./pages/Dashboard";
import TvMode from "./pages/TvMode";
import Documents from "./pages/Documents";
import DocReturns from "./pages/DocReturns";
import StudentPortal from "./pages/StudentPortal";
import MobileMonitoring from "./pages/MobileMonitoring";
import Funding from "./pages/Funding";
import AiChat from "./pages/AiChat";
import Savings from "./pages/Savings";
import SHU from "./pages/SHU";
import WhatsApp from "./pages/WhatsApp";
import Guide from "./pages/Guide";
import ImportExport from "./pages/ImportExport";
import LoanDetail from "./pages/LoanDetail";
import Login from "./pages/Login";
import MasterData from "./pages/MasterData";
import Matrix from "./pages/Matrix";
import NPL from "./pages/NPL";
import NplMapPage from "./pages/NplMapPage";
import Reports from "./pages/Reports";
import Payments from "./pages/Payments";
import InstallPage from "./pages/InstallPage";

function Guard({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Memuat sesi…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "siswa") return <Navigate to="/siswa-app" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PublicPortal() {
  const { token } = useParams();
  return <StudentPortal token={token} />;
}

function StudentGuard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Memuat sesi…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "siswa") return <Navigate to="/" replace />;
  return <StudentPortal />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/instal" element={<InstallPage />} />
      <Route path="/tv" element={<Guard><TvMode /></Guard>} />
      <Route path="/siswa-app" element={<StudentGuard />} />
      <Route path="/s/:token" element={<PublicPortal />} />
      <Route
        element={
          <Guard>
            <Layout />
          </Guard>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/siswa" element={<MasterData />} />
        <Route path="/pinjaman" element={<Navigate to="/siswa?tab=pinjaman" replace />} />
        <Route path="/pinjaman/:id" element={<LoanDetail />} />
        <Route path="/matriks" element={<Matrix />} />
        <Route path="/pembayaran" element={<Payments />} />
        <Route path="/kas" element={<Cash />} />
        <Route path="/kas-bulanan" element={<CashMonthly />} />
        <Route path="/simpanan" element={<Savings />} />
        <Route path="/shu" element={<SHU />} />
        <Route path="/whatsapp" element={<WhatsApp />} />
        <Route path="/pengeluaran" element={<Expenses />} />
        <Route path="/arus-kas" element={<CashFlow />} />
        <Route path="/approval" element={<Guard roles={WRITE_ROLES}><Approvals /></Guard>} />
        <Route path="/npl" element={<NPL />} />
        <Route path="/npl-map" element={<NplMapPage />} />
        <Route path="/dokumen" element={<Documents />} />
        <Route path="/pengembalian-dokumen" element={<DocReturns />} />
        <Route path="/import-export" element={<Guard roles={ADMIN_ROLES}><ImportExport /></Guard>} />
        <Route path="/panduan" element={<Guide />} />
        <Route path="/laporan" element={<Reports />} />
        <Route path="/admin" element={<Guard roles={["super_admin"]}><Admin /></Guard>} />
        <Route path="/monitoring-mobile" element={<Guard roles={ADMIN_ROLES}><MobileMonitoring /></Guard>} />
        <Route path="/funding" element={<Guard roles={["super_admin", "kepala_koperasi"]}><Funding /></Guard>} />
        <Route path="/asisten-ai" element={<Guard roles={["super_admin", "kepala_koperasi"]}><AiChat /></Guard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === "siswa" ? "/siswa-app" : "/"} replace />;
  return <Login />;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="yef-theme">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}
